import { ArrowBack, Description, ThumbDown, ThumbUp } from '@mui/icons-material';
import { Box, DialogContent, DialogTitle, Icon, IconButton, InputLabel, Stack, ToggleButton, useTheme } from '@mui/material';
import { createSubScore, getDisplayConfigFrom } from 'feature/report/AssessmentScore/SubScore/SubScoreLauncher';
import CheckpointForm from 'global/components/CheckpointForm';
import Confirm from 'global/components/confirm';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { first, get, isEqual, noop, set, size, sortBy } from 'global/lib/lodash';
import { getColorFromScore, useScreen } from 'global/styles';
import { getDisplayScoreLabel } from 'global/utils/getDisplayScore';
import { forwardRef, useEffect, useRef, useState } from 'react';
import sendData from 'redux/actions/sendData';
import { UploadFiles } from '../../../redux/actions/imageUploader';
import { useQueue } from '../../hooks/useQueue';
import Form from '../form';
import { Row } from '../row';
import ScoreIcon from '../ScoreIcon';
import { Text } from '../text';
import { BottomSpacing, BtnSave, DialogText, IconSpacing, RoundedCorners, SubScoreFormPadding, ToggleFormControl, ToggleLabel } from './styles';

// This Component enables the creation and editing of an Instance. It is structured hierarchically:
// 1. The **SubScoreToggle** is the smallest unit and functions independently.
// 2. The **SubScoreForm** includes SubScoreToggle.
// 3. The **SubScoreModal** is a modal that contains both the form and the button group, encapsulating all elements.
// 4. The **SubScoreTypeDisplay** is a lightweight, read-only version of the SubScoreToggle.

export const SubScoreToggle = (props) => {
  const { subScoreValueOptions } = props;
  const [subScoreValue, setSubScoreValue] = useState(props.subScoreValue);
  const theme = useTheme();

  const wrap = (label, Icon) => (label ? <IconSpacing as={Icon} /> : <Icon />);
  const toggleButtonIcons = [wrap(subScoreValueOptions[0].label, ThumbDown), wrap(subScoreValueOptions[1].label, ThumbUp), wrap(subScoreValueOptions[2].label, Description)];

  const getColorFromValue = (val, buttonValue) => {
    // If the current val matches the button val, then it is selected.
    const isSelected = val === buttonValue;
    const color = getColorFromScore(val);
    return {
      backgroundColor: isSelected ? color : undefined,
      color: isSelected ? theme.palette.getContrastText(color) : undefined,
    };
  };

  const handleChange = (e, newValue) => {
    if (newValue === null) return;
    setSubScoreValue(newValue);
    props.onChange && props.onChange(newValue);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
      }}
    >
      <RoundedCorners disabled={props.disabled} exclusive value={subScoreValue} onChange={handleChange}>
        {subScoreValueOptions.map((option, index) => (
          <ToggleButton key={index} value={option.value} style={getColorFromValue(subScoreValue, option.value)}>
            {toggleButtonIcons[index]}
            {option.label}
          </ToggleButton>
        ))}
      </RoundedCorners>
    </Box>
  );
};

export const SubScoreForm = forwardRef((props, ref) => {
  const { filteredRealms, assessment, scorecard, updateScores, setIsDirty } = props;
  const pssvTag = get(props, 'pssvTag', {});
  const pssvChecklist = pssvTag?.pssvChecklist || props.pssvChecklist;
  const pssvCheckpoint = pssvTag?.pssvCheckpoint || props.pssvCheckpoint;
  const pssvSystem = pssvTag?.pssvSystem || props.pssvSystem;
  const subScore = get(pssvTag, 'pssvSubScore', get(pssvCheckpoint, '_subscore'));
  const hasId = get(subScore, 'id', null);
  const [formState, setFormState] = useState(get(pssvTag, 'pssvSubScore', get(props, 'pssvCheckpoint', {})));
  const [initialFormState] = useState(get(pssvTag, 'pssvSubScore'));
  const [projectedScore, setProjectedScore] = useState(Number(pssvCheckpoint?.value));
  const [updateProjectedScore, setUpdateProjectedScore] = useState(false);
  const [canEditCheckpointForm, setCanEditCheckpointForm] = useState(false);
  const [isTradeSelectDirty, setIsTradeSelectDirty] = useState(false);
  const [defaultedTradeIds, setDefaultedTradeIds] = useState([]);
  // Checklist default trade ids, used on the edit form only to mute matching chips.
  const [checklistDefaultIds, setChecklistDefaultIds] = useState([]);
  const formRef = useRef();
  const submits = useRef(0);
  const sawSavedForm = useRef(false);
  const subScoreData = useRetrieveData(
    { url: `/sub-scores/${hasId}` },
    {
      waitFor: hasId,
      build: ['subScores'],
      transformData: (d) => first(d),
      params: {
        include: 'tradeTypes',
      },
    }
  );
  const savedId = hasId ? String(get(subScoreData, 'data.id', '')) : '';
  const [shownSavedId, setShownSavedId] = useState('');
  // Put the saved trades on the form before inputs mount, so their defaults are not a later edit.
  if (hasId && savedId && shownSavedId !== savedId && !subScoreData.loading) {
    setShownSavedId(savedId);
    setFormState((prev) => ({
      ...prev,
      tradeTypes: get(subScoreData, 'data.tradeTypes', prev.tradeTypes) || [],
    }));
  }
  const inputsReady = !hasId || (!subScoreData.loading && ((!!savedId && shownSavedId === savedId) || !savedId));
  const skipFormEcho = useRef(!!hasId);
  // Edit forms don't apply checklist defaults, so they don't wait on that load.
  const [tradecheckloaded, settradecheckloaded] = useState(!!hasId);
  const [checkpointFormQueue, setCheckpointFormQueue] = useState([]);
  const { addToQueue } = useQueue({ onUpdate: props.updateScores, system: pssvSystem });
  const { isView1, isView2, hasSubScores } = getDisplayConfigFrom(scorecard, props);
  const checkpointId = get(pssvCheckpoint, 'checkpointId');
  const config = get(scorecard, 'config');
  const allowScoreEditing = !(config?.hasSubScores === true && config?.editScoreInfo === false && config?.subScoringClass !== null);
  // If onFormReady exists, it means we're in checklist edit mode, and changes are submitted incrementally
  const onFormReady = get(props, 'onFormReady');
  const isContinuousEdit = !!onFormReady;
  const realm = get(props, 'realm', get(formState, 'realm'));
  const isEditMode = props.isEditMode || pssvTag?.isEditMode || false;

  const _setCheckpointFormQueue = (queue) => {
    setCheckpointFormQueue([queue]);
  };

  const subscoreScore = parseInt(get(formState, 'score', get(subScore, 'score', 0)));

  const getProjectedScore = async () => {
    const { data } = await sendData({ url: `/sub-scores/test-score`, method: 'post', data: submitSubscoreData });
    setProjectedScore(get(data, 'data.attributes.score'));
  };

  // The pause applies only after the saved instance is on screen. The render that
  // reveals those values is not an edit, so it does not start the timer.
  useEffect(() => {
    if (!isContinuousEdit || !inputsReady) return;
    if (!sawSavedForm.current) {
      sawSavedForm.current = true;
      return;
    }
    const timer = setTimeout(
      () => {
        submits.current += 1;
        props.onFormReady &&
          props.onFormReady({
            formState,
            submits: Math.max(submits.current, 2),
            submitSubScore,
          });
      },
      get(props, 'debounceTime', 1000)
    );
    return () => clearTimeout(timer);
  }, [formState, inputsReady]);

  useEffect(() => {
    const _fs = { ...formState, pssvTag };
    let hasUnsavedChanges;
    if (!!initialFormState) {
      // SubScore Edit Mode
      hasUnsavedChanges =
        (!props.canWallterSubscore && formState.notes !== null && !isEqual(formState.notes, initialFormState.notes)) ||
        !isEqual(formState.images, initialFormState.images) ||
        !isEqual(subscoreScore, Number(initialFormState.score)) ||
        isTradeSelectDirty;

      // Update projected score based on score change; once clicked, it never goes back to false.
      setUpdateProjectedScore(true);
    } else {
      // SubScore Create Mode
      hasUnsavedChanges = (!props.canWallterSubscore && !!formState?.notes) || !formState?.images === 0 || !(subscoreScore === 0) || isTradeSelectDirty;
      setUpdateProjectedScore(true);
    }
    setIsDirty?.(!!hasUnsavedChanges);
    props.onFormState && props.onFormState(_fs);
  }, [formState, pssvTag]);

  useEffect(() => {
    if (submitSubscoreData?.data?.attributes?.checkpoint_id && updateProjectedScore) {
      getProjectedScore();
    }
  }, [subscoreScore, updateProjectedScore]);

  // const _pssvCheckpoint = pssvCheckpoint || get(pssvTag, 'pssvCheckpoint');
  // By Location edit passes the library checklist, which has no category.
  // The checkpoint (and the sub-score copied from it) does.
  const superset_id = [get(pssvChecklist, 'supersetId'), get(pssvCheckpoint, 'supersetId'), get(subScore, 'supersetId'), get(subScore, 'pssvParentCheckpoint.supersetId')].find(
    (id) => id != null && id !== ''
  );
  const photoChecklistName = get(pssvChecklist, 'name') || get(pssvChecklist, 'checklist.name') || get(pssvCheckpoint, 'checklist.name') || get(subScore, 'checklist.name') || '';
  const photoScoreListProps = pssvCheckpoint
    ? {
        pssvCheckpoint: {
          isSubScore: true,
          value: Number.isNaN(subscoreScore) ? get(subScore, 'value', get(subScore, 'score')) : subscoreScore,
          name: get(pssvCheckpoint, 'name', get(subScore, 'name', '')),
          criteria: get(pssvCheckpoint, 'criteria', ''),
          checklist: { name: photoChecklistName },
          supersetId: superset_id,
        },
        pssvChecklist,
        assessment,
        scorecard,
        userCanModify: false,
      }
    : undefined;
  const assessmentRealmId = get(assessment, 'data.realmId');

  // If there's a label for each option, apply it.
  const subScoreValueLabel = get(scorecard, 'config.subScoreValueLabel') || [];
  const createSubScoreOptions = () => {
    const subScoreValueLabels = [];
    subScoreValueLabels.push({ value: 0, label: subScoreValueLabel.find((vl) => parseInt(vl.value) === 0)?.label });
    subScoreValueLabels.push({
      value: 100,
      label: subScoreValueLabel.find((vl) => parseInt(vl.value) === 100)?.label,
    });
    subScoreValueLabels.push({
      value: -1,
      label: subScoreValueLabel.find((vl) => parseInt(vl.value) === -1)?.label || 'Note',
    });
    return subScoreValueLabels;
  };
  const subScoreValueOptions = createSubScoreOptions();

  const disabled = !pssvCheckpoint || !realm || !((get(formState, 'notes') || '').trim() || !!size(get(formState, 'images'))) || get(formState, 'images', []).find((i) => i.loading);

  const name = get(pssvCheckpoint, 'name', '');
  const getTradeIds = (trades = []) => (trades || []).map((trade) => String(get(trade, 'id'))).filter((id) => id && id !== 'undefined' && id !== 'null');

  const [params, setParams] = useState([
    { url: `/checkpoints/${checkpointId}` },
    {
      useEffect: true,
      waitFor: checkpointId ? undefined : null,
      params: { include: 'scorecardOptions', filter: { scorecard_id: scorecard.id } },
      build: ['checkpoints'],
      transformData: (d) => get(d, '0'),
    },
  ]);
  const checkpointData = useRetrieveData(...params);

  useEffect(() => {
    if (checkpointId) {
      setParams((p) => [{ url: `/checkpoints/${checkpointId}` }, { ...p[1], waitFor: undefined }]);
    }
  }, [checkpointId]);

  useEffect(() => {
    setDefaultedTradeIds([]);
    setChecklistDefaultIds([]);
  }, [checkpointId, hasId]);

  useEffect(() => {
    setDefaultedTradeIds((prev) => {
      if (!size(prev)) {
        return prev;
      }

      const selectedTradeIds = new Set(getTradeIds(get(formState, 'tradeTypes', [])));
      const next = prev.filter((id) => selectedTradeIds.has(id));
      return next.length === prev.length ? prev : next;
    });
  }, [
    get(formState, 'tradeTypes', [])
      .map((trade) => get(trade, 'id'))
      .toString(),
  ]);

  const submitSubscoreData = {
    data: {
      type: 'sub-scores',
      id: get(subScore, 'id'),
      attributes: {
        superset_id: get(pssvCheckpoint, 'supersetId', '').toString(),
        assessment_id: get(props, 'assessment.data.id', '').toString(),
        scorecard_id: get(pssvCheckpoint, 'scorecardId', '').toString(),
        checklist_id: get(pssvCheckpoint, 'checklistId', '').toString(),
        checkpoint_id: get(pssvCheckpoint, 'checkpointId', '').toString(),
        scorecard_option_id: '',

        score: `${get(formState, 'score', 0)}`,
        criteria: get(pssvCheckpoint, 'criteria', ''),
        realm_id: get(formState, 'realm', props.realm?.id || undefined),
        notes: get(formState, 'notes', '') || '',
        images: get(formState, 'images', []),
      },
    },
  };

  const submitSubScore = async () => {
    const hasRealmId = get(formState, 'realm', props.realm?.id || undefined);

    const isNewRealm = !hasRealmId;
    if (isNewRealm) {
      const realmName = get(formState, 'realm.name', realm?.name);
      const result = await sendData(
        {
          url: `/realms`,
          method: 'post',
          data: {
            data: {
              type: 'realms',
              attributes: {
                name: realmName,
                tier: get(filteredRealms, 'realmFilter.tier', 'room'),
                parent_id: get(first(filteredRealms.data), 'parentId') || assessmentRealmId,
              },
            },
          },
        },
        { normalize: { build: ['realms'] } }
      );

      get(filteredRealms, 'update', () => 0)();
      // realm = first(result);
      props.setRealm && props.setRealm(first(result));
    }

    let data = submitSubscoreData;

    const tradeTypes = get(formState, 'tradeTypes');
    set(data, 'data.relationships.tradeTypes', {
      data: (tradeTypes || []).map((t, i) => ({ type: 'trade-types', id: t.id, pivot: { order: `${i}` } })),
    });

    await UploadFiles(data);
    return await createSubScore(data);
  };

  useEffect(() => {
    if (checkpointFormQueue.length > 0 && hasSubScores && isView2) {
      props.addToQueue ? props.addToQueue(first(checkpointFormQueue)) : addToQueue(first(checkpointFormQueue));
      setCheckpointFormQueue([]);
    }
  }, [checkpointFormQueue]);

  const handleFormSubmit = async () => {
    const result = await submitSubScore();

    // Update the subscore to the tree
    await Promise.all(result.map((s) => (updateScores ? updateScores(s) : noop())));

    // Update the checkpoint with the new checkpoint score.
    if (checkpointFormQueue.length > 0) {
      props.addToQueue ? await props.addToQueue(first(checkpointFormQueue)) : addToQueue(first(checkpointFormQueue));
    }

    setIsDirty && setIsDirty(false);
    props.onRightPanelClose?.();
    props.onClose && props.onClose(!hasId);
  };

  const handleEditCheckpointScore = () => {
    setCanEditCheckpointForm((prev) => !prev);
  };

  return (
    <BottomSpacing>
      <Form
        loading={checkpointData.loading || (hasId && !inputsReady)}
        showAllScreens={true}
        onFormState={(fst) => {
          // Inputs report their defaults once when they mount. The saved values are already loaded.
          if (skipFormEcho.current) {
            skipFormEcho.current = false;
            return;
          }
          setFormState((prev) => {
            const next = { ...prev, ...fst };
            // The location select reports an id for a realm object that is already loaded.
            if (next.realm != null && prev.realm && typeof prev.realm === 'object' && String(get(prev.realm, 'id')) === String(next.realm)) {
              next.realm = prev.realm;
            }
            return isEqual(prev, next) ? prev : next;
          });
        }}
        ref={formRef}
        screens={[
          {
            title: '',
            inputs: [
              {
                id: 'subscoreType',
                component: () => (
                  <>
                    <BottomSpacing as={Text} type="title_large">
                      <strong>
                        {get(pssvTag, 'pssvChecklist.name')}: {get(pssvTag, 'pssvCheckpoint.name', '')}
                      </strong>
                    </BottomSpacing>
                    <ToggleFormControl>
                      <ToggleLabel as={InputLabel}>Instance Type</ToggleLabel>
                      <SubScoreToggle
                        subScoreValue={subscoreScore}
                        subScoreValueOptions={subScoreValueOptions}
                        disabled={!tradecheckloaded}
                        onChange={(f) =>
                          setFormState((fs) => ({
                            ...fs,
                            score: f,
                          }))
                        }
                      />
                    </ToggleFormControl>
                  </>
                ),
              },
              isEditMode && {
                id: 'realm',
                type: 'Select',
                data: () => (filteredRealms?.data || props.realms?.data || []).map((d) => ({ title: d.name, value: d.id })),
                defaultValue: () => get(formState, 'realm.id'),
                props: {
                  label: 'Location',
                },
              },
              {
                id: 'notes',
                type: 'TextField',
                defaultValue: () => get(formState, 'notes'),
                props: {
                  placeholder: 'Describe your observation.',
                  rows: 5,
                  multiline: true,
                },
              },
              {
                id: 'images',
                type: 'DropArea',
                defaultValue: () => sortBy(get(formState, 'images', []), 'orderColumn'),
                props: {
                  label: 'Images',
                  sortable: true,
                  shouldUpdateOnCaptionChange: true,
                  notAllowed: ['pssvTag'],
                  parentId: hasId,
                  uploadAllNew: true,
                  collection: 'sub-score-images',
                  imageSelect: {
                    assessment: get(props, 'assessment.data'),
                    superset_id: superset_id,
                    disabled: !superset_id,
                    scoreListProps: photoScoreListProps,
                  },
                },
              },
              {
                id: 'referenceScore',
                component: () => (
                  <ToggleFormControl>
                    <ToggleLabel as={InputLabel}>Reference Score</ToggleLabel>
                    <Row spaceBetween dontCenter>
                      {canEditCheckpointForm ? (
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                          <CheckpointForm
                            pssvCheckpoint={get(pssvCheckpoint, 'pssvParentCheckpoint', pssvCheckpoint)}
                            pssvChecklist={pssvChecklist}
                            assessmentId={get(assessment, 'data.id')}
                            assessment={assessment}
                            checkpointdata={checkpointData}
                            loading={checkpointData.loading}
                            scorecard={scorecard}
                            canEditInfo={hasSubScores ? isView1 : undefined}
                            hideInfoIcon={true}
                            setNeedUpdate={false}
                            isCourse={props.isCourse}
                            addToQueue={_setCheckpointFormQueue}
                            queue={checkpointFormQueue}
                            submitCompleted={submits.current}
                          />
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                          }}
                        >
                          <Text variant="body2">
                            {getDisplayScoreLabel(
                              {
                                checklistId: pssvCheckpoint?.checklistId,
                                supersetId: pssvCheckpoint?.supersetId,
                                value: projectedScore,
                              },
                              0,
                              scorecard?.config
                            )}
                          </Text>
                          <ScoreIcon score={projectedScore} />
                          <div>
                            {
                              <Text variant={'body1'}>
                                <strong>{name}</strong> {pssvCheckpoint?.criteria || ''}
                              </Text>
                            }
                          </div>
                        </Box>
                      )}

                      {allowScoreEditing && (
                        <IconButton onClick={handleEditCheckpointScore}>
                          <Icon>{canEditCheckpointForm ? 'close' : 'edit'}</Icon>
                        </IconButton>
                      )}
                    </Row>
                  </ToggleFormControl>
                ),
              },
              {
                id: 'tradeTypes',
                type: 'TradeSelect',
                defaultValue: () => get(subScoreData, 'data.tradeTypes', []),
                props: {
                  checklist: get(pssvTag, 'pssvChecklist'),
                  onChecklistTradesLoaded: (trades = []) => {
                    const defaultIds = getTradeIds(trades);
                    if (hasId) {
                      // Edit: remember the checklist default so matching chips can be muted.
                      // Do not write it onto the instance.
                      setChecklistDefaultIds(defaultIds);
                      return;
                    }
                    if (!size(get(formState, 'tradeTypes', []))) {
                      setDefaultedTradeIds(defaultIds);
                    }
                    setTimeout(() => settradecheckloaded(true), 500);
                  },
                  setIsTradeSelectDirty: setIsTradeSelectDirty,
                  skipDefaults: !!hasId,
                  mutedTradeIds: hasId
                    ? checklistDefaultIds.filter((id) => {
                        const assigned = isTradeSelectDirty ? get(formState, 'tradeTypes', []) : get(subScoreData, 'data.tradeTypes', get(formState, 'tradeTypes', []));
                        return getTradeIds(assigned).includes(id);
                      })
                    : defaultedTradeIds,
                },
              },
            ].filter((f) => !!f),
          },
        ]}
      />
      {!isContinuousEdit && (
        <BtnSave onClick={handleFormSubmit} variant="contained" color="secondary" disabled={disabled} icon="check" data-testid="instance-save-button">
          Save
        </BtnSave>
      )}
    </BottomSpacing>
  );
});

const SubScoreModal = (props) => {
  const { onClose, open, isDialog, pssvTag, isDirty, setIsDirty } = props;
  const isEditMode = props.pssvTag?.isEditMode || false;
  const { isMobile } = useScreen();
  const [conf, setConf] = useState({});
  const elementId = get(pssvTag, 'pssvSubScore.id', get(pssvTag, 'pssvCheckpoint.id'));

  const handleClose = () => {
    if (isDirty) {
      setConf({
        title: 'Unsaved changes',
        content: 'You have unsaved data, are you sure you want to close?',
        onOk: () => {
          setConf({});
          props.onRightPanelClose && props.onRightPanelClose();
          setIsDirty(false);
          onClose && onClose();
        },
        onCancel: () => {
          setConf({});
        },
      });
    } else {
      props.onRightPanelClose && props.onRightPanelClose();
      onClose && onClose();
    }
  };

  return (
    <>
      {isDialog ? (
        <DialogText open={open} onClose={handleClose} maxWidth="md" fullWidth fullScreen={isMobile}>
          <Box sx={{ display: 'flex', alignItems: 'center', marginLeft: { xs: 1, md: 3 }, marginTop: 3 }}>
            <IconButton onClick={handleClose} data-testid="instance-modal-close-button">
              <ArrowBack />
            </IconButton>
            <Text type="title_large">{isEditMode ? 'Edit Instance' : 'Create Instance'}</Text>
          </Box>
          <DialogContent>
            <SubScoreForm key={elementId} {...props} isDirty={isDirty} setIsDirty={setIsDirty} isEditMode={isEditMode} />
          </DialogContent>
        </DialogText>
      ) : (
        open && (
          <>
            <Box sx={{ marginLeft: { xs: 1, md: 3, lg: 1, xl: 3 }, display: 'flex', alignItems: 'center', marginTop: 3 }}>
              <IconButton onClick={handleClose} data-testid="instance-panel-close-button">
                <ArrowBack />
              </IconButton>
              <Text type="title_large">{isEditMode ? 'Edit Instance' : 'Create Instance'}</Text>
            </Box>
            <SubScoreFormPadding sx={{ flex: 1, overflow: 'auto' }}>
              <SubScoreForm key={elementId} {...props} isDirty={isDirty} setIsDirty={setIsDirty} isEditMode={isEditMode} />
            </SubScoreFormPadding>
          </>
        )
      )}
      <Confirm {...conf} />
    </>
  );
};

export default SubScoreModal;
